import { useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Images, Loader2 } from "lucide-react";
import { api, uploadImage, assetUrl } from "@/lib/api";
import { CardSide } from "@/components/card/CardSide";
import { withSystem, verifyUrl } from "@/lib/card";
import { ExternalLink } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const stem = (s) => String(s || "").trim().toLowerCase().replace(/\.[a-z0-9]+$/, "");

export default function RecordsTable({ tpl, fields, records, reload, onEdit, editingId }) {
  const photoRef = useRef(null);
  const [matching, setMatching] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const imageFields = fields.filter((f) => f.type === "image");

  const remove = async (rid) => { await api.delete(`/records/${rid}`); toast.success("Record deleted"); reload(); };
  const clearAll = async () => { await api.delete(`/templates/${tpl.id}/records`); setConfirmClear(false); toast.success("All records cleared"); reload(); };

  const matchPhotos = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    setMatching(true);
    const byName = new Map(files.map((f) => [stem(f.name), f]));
    let n = 0;
    try {
      for (const r of records) {
        const patch = {};
        for (const f of imageFields) {
          const file = byName.get(stem(r.data[f.key]));
          if (file) patch[f.key] = await uploadImage(file);
        }
        if (Object.keys(patch).length) { await api.put(`/records/${r.id}`, { data: { ...r.data, ...patch } }); n++; }
      }
      toast.success(`Matched photos for ${n} record${n === 1 ? "" : "s"}`);
      reload();
    } catch (err) { toast.error("Photo matching failed"); } finally { setMatching(false); }
  };

  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900/60" data-testid="records-section">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-800">
        <div>
          <p className="font-heading font-bold">Records <span className="text-slate-500 font-mono text-sm" data-testid="records-count">({records.length})</span></p>
          {imageFields.length > 0 && <p className="text-[11px] text-slate-500 mt-0.5">Bulk photos: put the file name (e.g. <span className="font-mono">emp001.jpg</span>) in the {imageFields.map((f) => f.key).join(" / ")} column, then use “Match photos”.</p>}
        </div>
        <div className="flex gap-2">
          {imageFields.length > 0 && (
            <button className="cf-btn-ghost h-8 text-xs" onClick={() => photoRef.current?.click()} disabled={matching || !records.length} data-testid="match-photos-button">
              {matching ? <Loader2 size={13} className="animate-spin" /> : <Images size={13} />} Match photos
            </button>
          )}
          <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" onChange={matchPhotos} data-testid="match-photos-input" />
          {records.length > 0 && <button className="cf-btn-ghost h-8 text-xs hover:text-red-400" onClick={() => setConfirmClear(true)} data-testid="clear-records-button"><Trash2 size={13} /> Clear all</button>}
        </div>
      </div>
      {records.length === 0 ? (
        <p className="p-8 text-center text-sm text-slate-500" data-testid="records-empty">No records yet. Add a card above or import a spreadsheet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="bulk-data-table">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
                <th className="px-4 py-3 w-10">#</th>
                <th className="px-4 py-3">Card</th>
                {fields.map((f) => <th key={f.key} className="px-4 py-3 whitespace-nowrap">{f.key}</th>)}
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={r.id} className={`border-b border-slate-800/60 hover:bg-slate-800/30 ${editingId === r.id ? "bg-amber-500/5" : ""}`} data-testid={`record-row-${i}`}>
                  <td className="px-4 py-2 font-mono text-slate-500">{i + 1}</td>
                  <td className="px-4 py-2"><div className="rounded overflow-hidden w-fit ring-1 ring-slate-700"><CardSide tpl={tpl} data={withSystem(r)} scale={Math.min(64 / tpl.width, 40 / tpl.height)} /></div></td>
                  {fields.map((f) => (
                    <td key={f.key} className="px-4 py-2 max-w-[180px] truncate text-slate-300">
                      {f.type === "image" && r.data[f.key] && /^(\/api\/|https?:)/.test(r.data[f.key])
                        ? <img src={assetUrl(r.data[f.key])} alt="" className="w-8 h-8 rounded object-cover" />
                        : String(r.data[f.key] ?? "")}
                    </td>
                  ))}
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {(tpl.public_verify ?? tpl.category === "jewellery") && (
                      <a href={verifyUrl(r.id)} target="_blank" rel="noreferrer" className="inline-block p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-amber-300" title="Open public verify page" data-testid={`record-verify-link-${i}`}><ExternalLink size={13} /></a>
                    )}
                    <button onClick={() => onEdit(r)} className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-white" data-testid={`record-edit-${i}`}><Pencil size={13} /></button>
                    <button onClick={() => remove(r.id)} className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-red-400" data-testid={`record-delete-${i}`}><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all {records.length} records?</AlertDialogTitle>
            <AlertDialogDescription>The design stays; only the card data is removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={clearAll} className="bg-red-600 hover:bg-red-500" data-testid="clear-confirm-button">Clear</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
